import { useCallback, useState } from 'react';

import { Box, Slider, Stack, Typography } from '@mui/material';

import { useLocale } from '@shared/hooks/locale';

type ZoomLevelSliderProps = {
  value: number | null;
  defaultValue: number;
  onChange: (value: number) => Promise<void>;
  disabled?: boolean;
  label?: string;
};

function ZoomLevelSlider({
  value,
  defaultValue,
  onChange,
  disabled = false,
  label,
}: ZoomLevelSliderProps): React.ReactElement {
  const { t } = useLocale();
  const [localZoomLevel, setLocalZoomLevel] = useState<number | null>(null);

  const displayedZoomLevel = localZoomLevel ?? value ?? defaultValue;

  const handleChange = useCallback((_: Event, newValue: number | number[]) => {
    setLocalZoomLevel(Array.isArray(newValue) ? newValue[0] ?? 0 : newValue);
  }, []);

  const handleCommit = useCallback(
    async (
      _event: React.SyntheticEvent | Event,
      newValue: number | number[],
    ) => {
      const zoomValue = Array.isArray(newValue)
        ? newValue[0] ?? displayedZoomLevel
        : newValue;
      await onChange(zoomValue);
      setLocalZoomLevel(null);
    },
    [displayedZoomLevel, onChange],
  );

  return (
    <Box sx={{ p: 2, pt: 0, mt: 0 }}>
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
          }}
        >
          {label ?? t('Zoom level')}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
          }}
        >
          {displayedZoomLevel}
        </Typography>
      </Stack>
      <Box sx={{ px: 1 }}>
        <Slider
          value={displayedZoomLevel}
          onChange={handleChange}
          onChangeCommitted={handleCommit}
          disabled={disabled}
          min={10}
          max={18}
          marks
          valueLabelDisplay="auto"
        />
      </Box>
    </Box>
  );
}

export default ZoomLevelSlider;
